import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate } from "remotion";

// 效果 43: SVG 描边动画
export const SVGStrokeAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [0, 90], [0, 100], {
    extrapolateRight: "clamp",
  });
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0f23",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg width="400" height="400" viewBox="0 0 400 400">
        <path
          d="M 100 200 L 150 100 L 200 200 L 250 150 L 300 200 L 350 100"
          stroke="#4ecdc4"
          strokeWidth="4"
          fill="none"
          strokeDasharray="500"
          strokeDashoffset={500 - (progress / 100) * 500}
          style={{
            filter: "drop-shadow(0 0 10px rgba(78, 205, 196, 0.8))",
          }}
        />
        <circle
          cx={100 + (progress / 100) * 250}
          cy={200}
          r="8"
          fill="#ff6b6b"
          style={{
            filter: "drop-shadow(0 0 10px rgba(255, 107, 107, 0.8))",
          }}
        />
      </svg>
    </AbsoluteFill>
  );
};

// 效果 44: 路径跟随动画
export const PathFollowAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#1a1a2e",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg width="600" height="400" viewBox="0 0 600 400">
        <path
          d="M 50 200 Q 150 100 250 200 T 450 200 T 550 200"
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth="2"
          fill="none"
        />
        {Array.from({ length: 10 }, (_, i) => {
          const offset = (frame + i * 10) % 100;
          const x = interpolate(offset, [0, 100], [50, 550]);
          const y = 200 + Math.sin((x / 100) * Math.PI * 2) * 50;
          
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={10 - i * 0.5}
              fill={`hsl(${(i * 36 + frame) % 360}, 70%, 60%)`}
              style={{
                filter: `drop-shadow(0 0 ${10 - i}px currentColor)`,
              }}
            />
          );
        })}
      </svg>
    </AbsoluteFill>
  );
};

// 效果 45: 形态变换 (Morphing)
export const MorphingShapes: React.FC = () => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [0, 60, 120], [0, 1, 0], {
    extrapolateRight: "clamp",
  });
  
  const sides = Math.round(interpolate(progress, [0, 1], [3, 8]));
  const points = Array.from({ length: sides }, (_, i) => {
    const angle = (i / sides) * Math.PI * 2 - Math.PI / 2;
    const x = 300 + Math.cos(angle) * 100;
    const y = 200 + Math.sin(angle) * 100;
    return `${x},${y}`;
  }).join(" ");
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#000",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg width="600" height="400" viewBox="0 0 600 400">
        <polygon
          points={points}
          fill="none"
          stroke={`hsl(${interpolate(frame, [0, 120], [0, 360])}, 70%, 50%)`}
          strokeWidth="3"
          style={{
            filter: "drop-shadow(0 0 20px currentColor)",
          }}
        />
      </svg>
    </AbsoluteFill>
  );
};

// 效果 46: 数据可视化 - 柱状图
export const BarChartAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const data = [85, 92, 78, 95, 88, 72, 90];
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0f23",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg width="800" height="400" viewBox="0 0 800 400">
        {data.map((value, i) => {
          const height = interpolate(
            frame,
            [i * 10, i * 10 + 30],
            [0, value * 3],
            { extrapolateRight: "clamp" }
          );
          const hue = (i * 360) / data.length;
          
          return (
            <g key={i}>
              <rect
                x={100 + i * 90}
                y={350 - height}
                width={70}
                height={height}
                fill={`hsl(${hue}, 70%, 50%)`}
                rx="5"
                style={{
                  filter: `drop-shadow(0 0 10px hsl(${hue}, 70%, 50%))`,
                }}
              />
              <text
                x={135 + i * 90}
                y={380}
                textAnchor="middle"
                fill="white"
                fontSize="20"
              >
                {`Day ${i + 1}`}
              </text>
            </g>
          );
        })}
      </svg>
    </AbsoluteFill>
  );
};

// 效果 47: 数字滚动
export const NumberCounter: React.FC = () => {
  const frame = useCurrentFrame();
  const number = Math.floor(interpolate(frame, [0, 90], [0, 9999], {
    extrapolateRight: "clamp",
  }));
  const formattedNumber = number.toString().padStart(4, "0");
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#1a1a2e",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ display: "flex", fontSize: 120, fontFamily: "monospace" }}>
        {formattedNumber.split("").map((digit, i) => (
          <div
            key={i}
            style={{
              color: "#4ecdc4",
              margin: "0 5px",
              padding: "10px 20px",
              backgroundColor: "rgba(78, 205, 196, 0.1)",
              borderRadius: "10px",
              boxShadow: "0 0 20px rgba(78, 205, 196, 0.3)",
            }}
          >
            {digit}
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

// 效果 48: 加载动画 - 旋转环
export const LoadingSpinner: React.FC = () => {
  const frame = useCurrentFrame();
  const rotation = frame * 10;
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0f23",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg width="200" height="200" viewBox="0 0 200 200">
        {Array.from({ length: 12 }, (_, i) => {
          const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const x = 100 + Math.cos(angle) * 70;
          const y = 100 + Math.sin(angle) * 70;
          const opacity = interpolate(
            (frame + i * 3) % 36,
            [0, 36],
            [0.3, 1]
          );
          
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="8"
              fill={`rgba(78, 205, 196, ${opacity})`}
            />
          );
        })}
      </svg>
    </AbsoluteFill>
  );
};
